import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const PROVINCES = ["Québec", "Ontario", "Alberta", "Colombie-Britannique", "Manitoba", "Saskatchewan", "Nouveau-Brunswick", "Nouvelle-Écosse"];

export default function RequestService() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    province: "",
    city: "",
    category: params.get("category") || "",
    description: "",
    urgency: "moyenne",
    budget: "",
    contact_method: "email",
    profile_id: params.get("profile_id") || undefined,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/requests", form);
      navigate(`/merci?type=demande&name=${encodeURIComponent(form.name)}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="request-service-page">
      <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">Demande de service</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-foreground tracking-tight">Décrivez votre besoin</h1>
      <p className="text-muted-foreground mt-2">Nous vous mettrons en relation avec le bon prestataire.</p>

      <form onSubmit={submit} className="card-surface p-6 sm:p-8 mt-8 space-y-5" data-testid="request-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Nom complet</label>
            <input required value={form.name} onChange={(e) => update("name", e.target.value)} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="req-name" />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Email</label>
            <input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="req-email" />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Téléphone</label>
            <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="req-phone" />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Province</label>
            <select value={form.province} onChange={(e) => update("province", e.target.value)} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="req-province">
              <option value="">—</option>
              {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Ville</label>
            <input value={form.city} onChange={(e) => update("city", e.target.value)} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="req-city" />
          </div>
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Catégorie souhaitée</label>
          <select value={form.category} onChange={(e) => update("category", e.target.value)} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="req-category">
            <option value="">—</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Description du besoin</label>
          <textarea required rows={5} value={form.description} onChange={(e) => update("description", e.target.value)} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="req-description" />
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Urgence</label>
            <select value={form.urgency} onChange={(e) => update("urgency", e.target.value)} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="req-urgency">
              <option value="faible">Faible</option>
              <option value="moyenne">Moyenne</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Budget estimé</label>
            <input value={form.budget} onChange={(e) => update("budget", e.target.value)} placeholder="ex. 500$ - 1000$" className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="req-budget" />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Méthode de contact</label>
            <select value={form.contact_method} onChange={(e) => update("contact_method", e.target.value)} className="w-full mt-1 px-4 py-3 border border-emerald-900/10 rounded-xl text-sm outline-none focus:border-primary" data-testid="req-contact">
              <option value="email">Email</option>
              <option value="telephone">Téléphone</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
        </div>

        <button type="submit" disabled={loading} className="btn-primary w-full sm:w-auto justify-center" data-testid="req-submit">
          {loading ? "Envoi…" : "Envoyer la demande"} <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
